/**
 * Handles add-to-cart from cart drawer cross-sell forms: POST to /cart/add.js with
 * sections so the drawer re-renders, then dispatches CartAddEvent for morphing.
 * Also handles variant dropdown changes to update price, image, and form inputs.
 */
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

const FORM_SELECTOR = '.cart-drawer-cross-sell__form';
const VARIANT_SELECT_SELECTOR = '[data-cross-sell-variant-select]';
const COLOR_TRIGGER_SELECTOR = '[data-cross-sell-color-trigger]';
const COLOR_LIST_SELECTOR = '[data-cross-sell-color-list]';
const COLOR_OPTION_SELECTOR = '[data-cross-sell-color-option]';

/**
 * Strip HTML tags from a string (e.g. from Liquid money filters that output trans-money spans).
 * @param {string} str
 * @returns {string}
 */
function stripHtml(str) {
  if (typeof str !== 'string') return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = str;
  return tmp.textContent || tmp.innerText || '';
}

/**
 * Format a price in cents using the shop money format.
 * Falls back to $X.XX if no format is available.
 * Always returns plain text (no HTML).
 * @param {number} cents
 * @returns {string}
 */
function formatPrice(cents) {
  const amount = (cents / 100).toFixed(2);
  const moneyFormat = document.querySelector('[data-cross-sell-money-format]')?.dataset.crossSellMoneyFormat;
  let formatted;
  if (moneyFormat) {
    formatted = moneyFormat.replace(/\{\{\s*amount\s*\}\}/g, amount);
  } else {
    formatted = `$${amount}`;
  }
  return stripHtml(formatted);
}

function handleVariantChange(event) {
  const select = event.target.closest(VARIANT_SELECT_SELECTOR);
  if (!select) return;

  const item = select.closest('[data-cross-sell-item]');
  if (!item) return;

  const selectedOption = select.options[select.selectedIndex];
  const variantId = select.value;
  const price = Number(selectedOption.dataset.price);
  const comparePrice = Number(selectedOption.dataset.comparePrice);
  const available = selectedOption.dataset.available === 'true';
  const imageUrl = selectedOption.dataset.image;

  // Update hidden variant input and form data attribute
  const variantInput = item.querySelector('[data-cross-sell-variant-input]');
  if (variantInput) variantInput.value = variantId;

  const form = item.querySelector(FORM_SELECTOR);
  if (form) form.dataset.variantId = variantId;

  // Update price display (formatPrice returns plain text, strips any HTML from money format)
  const priceWrap = item.querySelector('[data-cross-sell-price-wrap]');
  if (priceWrap) {
    const currentPriceEl = priceWrap.querySelector('[data-cross-sell-current-price]');
    const comparePriceEl = priceWrap.querySelector('[data-cross-sell-compare-price]');

    if (currentPriceEl) currentPriceEl.textContent = formatPrice(price);

    if (comparePrice > price) {
      if (comparePriceEl) {
        comparePriceEl.textContent = formatPrice(comparePrice);
        comparePriceEl.style.display = '';
      } else {
        const s = document.createElement('s');
        s.className = 'cart-drawer-cross-sell__compare-price';
        s.setAttribute('data-cross-sell-compare-price', '');
        s.textContent = formatPrice(comparePrice);
        priceWrap.insertBefore(s, currentPriceEl);
      }
    } else if (comparePriceEl) {
      comparePriceEl.style.display = 'none';
    }
  }

  // Update image if variant has one
  if (imageUrl) {
    const img = item.querySelector('[data-cross-sell-image]');
    if (img) img.src = imageUrl;
  }

  // Toggle add button availability
  const submitButton = item.querySelector('.cart-drawer-cross-sell__add');
  if (submitButton) submitButton.disabled = !available;
}

function handleCrossSellSubmit(event) {
  const form = event.target?.closest?.(FORM_SELECTOR);
  if (!form) return;

  event.preventDefault();

  const sectionId = form.dataset.drawerSectionId;
  const idInput = form.querySelector('input[name="id"]');
  const variantId = idInput?.value;
  if (!variantId || !sectionId) return;

  const submitButton = form.querySelector('button[type="submit"]');
  const wasDisabled = submitButton?.disabled;
  if (submitButton) {
    submitButton.disabled = true;
    submitButton.setAttribute('aria-busy', 'true');
  }

  const body = JSON.stringify({
    items: [{ id: Number(variantId), quantity: 1 }],
    sections: sectionId,
    sections_url: window.location.pathname,
  });

  fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
    ...fetchConfig('json', { body }),
    body,
  })
    .then((res) => res.json())
    .then((data) => {
      if (data.status && data.status !== 200) {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.removeAttribute('aria-busy');
        }
        return;
      }
      if (data.sections) {
        document.dispatchEvent(
          new CartAddEvent({}, form.id || 'cart-drawer-cross-sell', {
            source: 'cart-drawer-cross-sell',
            itemCount: 1,
            variantId: String(variantId),
            sections: data.sections,
            addedItems: data.items ?? [],
          })
        );
      }
      if (submitButton) {
        submitButton.disabled = wasDisabled ?? false;
        submitButton.removeAttribute('aria-busy');
      }
    })
    .catch(() => {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
      }
    });
}

function closeAllColorLists() {
  document.querySelectorAll(COLOR_LIST_SELECTOR).forEach((list) => {
    list.hidden = true;
    const trigger = list.parentElement?.querySelector(COLOR_TRIGGER_SELECTOR);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  });
}

function handleColorTriggerClick(event) {
  const trigger = event.target.closest(COLOR_TRIGGER_SELECTOR);
  if (!trigger) return;

  event.preventDefault();
  const wrapper = trigger.closest('.cart-drawer-cross-sell__variant-select-wrapper--has-swatches');
  const list = wrapper?.querySelector(COLOR_LIST_SELECTOR);
  if (!list) return;

  const isOpen = !list.hidden;
  closeAllColorLists();
  if (!isOpen) {
    list.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
  }
}

function handleColorOptionClick(event) {
  const option = event.target.closest(COLOR_OPTION_SELECTOR);
  if (!option) return;

  event.preventDefault();
  const wrapper = option.closest('[data-cross-sell-item]');
  const select = wrapper?.querySelector(VARIANT_SELECT_SELECTOR);
  const trigger = wrapper?.querySelector(COLOR_TRIGGER_SELECTOR);
  const list = wrapper?.querySelector(COLOR_LIST_SELECTOR);

  if (!select || !trigger || !list) return;

  const variantId = option.dataset.variantId;
  select.value = variantId;
  select.dispatchEvent(new Event('change', { bubbles: true }));

  const optionSwatch = option.querySelector('.cart-drawer-cross-sell__color-swatch');
  const optionLabel = option.querySelector('.cart-drawer-cross-sell__color-label');
  const triggerSwatch = trigger.querySelector('.cart-drawer-cross-sell__color-swatch');
  const triggerLabel = trigger.querySelector('.cart-drawer-cross-sell__color-label');

  if (optionSwatch && triggerSwatch) triggerSwatch.style.cssText = optionSwatch.style.cssText;
  if (optionLabel && triggerLabel) triggerLabel.textContent = optionLabel.textContent;

  list.querySelectorAll(COLOR_OPTION_SELECTOR).forEach((el) => el.setAttribute('aria-selected', 'false'));
  option.setAttribute('aria-selected', 'true');

  list.hidden = true;
  trigger.setAttribute('aria-expanded', 'false');
}

function handleDocumentClick(event) {
  if (event.target.closest('.cart-drawer-cross-sell__variant-select-wrapper--has-swatches')) return;
  closeAllColorLists();
}

function handleKeydown(event) {
  if (event.key === 'Escape') closeAllColorLists();
}

function init() {
  document.addEventListener('submit', handleCrossSellSubmit);
  document.addEventListener('change', handleVariantChange);
  document.addEventListener('click', handleColorTriggerClick);
  document.addEventListener('click', handleColorOptionClick);
  document.addEventListener('click', handleDocumentClick);
  document.addEventListener('keydown', handleKeydown);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
